import { useState, useMemo } from "react";
import { Box } from "@mui/material";
import {
    Business as CompaniesIcon,
    Inventory2 as ResourcesIcon,
} from "@mui/icons-material";
import { RTL } from "../../cache/CacheRtl";
import CompanyOverviewKpiRow from "./CompanyOverviewKpiRow";
import ArboxCollectionsRow from "./ArboxCollectionsRow";
import DashboardTable from "./coach/DashboardTable";
import TabsRow from "./coach/TabsRow";
import CompaniesTable, { COMPANY_TABS } from "./CompaniesTable";
import CompaniesTableFilters from "./CompaniesTableFilters";
import { t } from "./coach/coachTokens";

// Two coach-style tabs: companies overview vs. per-company resource counts.
const TAB_ICONS = {
    companies: CompaniesIcon,
    resources: ResourcesIcon,
};
const TABS = COMPANY_TABS.map(({ key, label }) => ({ key, label, Icon: TAB_ICONS[key] }));

// `arboxCollections` is super-admin-only and may be null (group admin, or the fetch failed) — the KPI row
// simply drops the tile in that case.
const CompanyBreakdown = ({ breakdownStats, onCompanySelect, showSubscription = true, arboxCollections = null }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [tab, setTab] = useState("companies");
    const [statusFilter, setStatusFilter] = useState(null); // null | "active" | "inactive"
    const [planFilter, setPlanFilter] = useState(null);      // null | subscriptionPlan value

    const filteredCompanies = useMemo(() => {
        let list = breakdownStats?.companies || [];

        const term = searchTerm.trim().toLowerCase();
        // Guard against companies with a null/undefined name — dereferencing c.name
        // directly here threw a TypeError and crashed the whole table on search.
        if (term) list = list.filter((c) => (c.name || "").toLowerCase().includes(term));

        if (statusFilter === "active") list = list.filter((c) => c.enabled);
        else if (statusFilter === "inactive") list = list.filter((c) => !c.enabled);

        if (planFilter) list = list.filter((c) => c.subscriptionPlan === planFilter);

        return list;
    }, [breakdownStats?.companies, searchTerm, statusFilter, planFilter]);

    if (!breakdownStats) return null;

    return (
        <RTL>
        <Box dir="rtl" sx={{ background: t.bg }}>
            {/* Operator's own Arbox money — its own row, above the platform metrics. Renders
                nothing at all unless a super admin supplied it. */}
            <ArboxCollectionsRow collections={arboxCollections} />

            {/* Aggregate KPI row — system-level superadmin metrics */}
            <CompanyOverviewKpiRow stats={breakdownStats} />

            {/* Coach-style tabs — switch the table's focused metric */}
            <TabsRow value={tab} onChange={setTab} tabs={TABS} />

            {/* Companies table — coach-style sortable table */}
            <DashboardTable
                count={filteredCompanies.length}
                search={searchTerm}
                onSearchChange={setSearchTerm}
                extraFilters={
                    <CompaniesTableFilters
                        status={statusFilter}
                        onStatus={setStatusFilter}
                        plan={planFilter}
                        onPlan={setPlanFilter}
                    />
                }
            >
                <CompaniesTable
                    companies={filteredCompanies}
                    onCompanySelect={onCompanySelect}
                    tab={tab}
                    showSubscription={showSubscription}
                />
            </DashboardTable>
        </Box>
        </RTL>
    );
};

export default CompanyBreakdown;
